import { useCallback, useEffect, useRef, useState } from 'react';
import { reviewService } from '@/services';
import type { Email } from '@/types';

export interface UseReviewItemSourcesResult {
  emails: Email[];
  summary: string | null;
  loading: boolean;
  error: Error | null;
  reload(): void;
}

interface LoadState {
  /** The read this answers: the ids plus the reload count. */
  for: string;
  emails: Email[];
  summary: string | null;
  error: Error | null;
}

const NO_EMAILS: Email[] = [];

/**
 * The details sheet's Sources (YAR-551 D2): an item's source emails and the
 * newest one's Smart Summary.
 *
 * Loads when `emailIds` is non-null; a later id set supersedes an in-flight
 * read (latest wins). Ids are compared by value, so a new array holding the
 * same ids does not read again.
 */
export function useReviewItemSources(
  emailIds: readonly string[] | null,
): UseReviewItemSourcesResult {
  const ids = emailIds ? emailIds.join(',') : null;
  const [reloads, setReloads] = useState(0);
  const [state, setState] = useState<LoadState | null>(null);
  const requestRef = useRef(0);
  const key = ids === null ? null : `${ids}#${reloads}`;

  useEffect(() => {
    // Invalidates any read still in flight for a previous id set.
    const request = ++requestRef.current;
    if (ids === null || key === null) return;
    const wanted = ids === '' ? [] : ids.split(',');
    reviewService.getReviewItemSources(wanted).then(
      (sources) => {
        if (request !== requestRef.current) return;
        setState({ for: key, ...sources, error: null });
      },
      (err: unknown) => {
        if (request !== requestRef.current) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ for: key, emails: NO_EMAILS, summary: null, error });
      },
    );
  }, [ids, key]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  const current = key !== null && state?.for === key ? state : null;
  return {
    emails: current?.emails ?? NO_EMAILS,
    summary: current?.summary ?? null,
    loading: key !== null && current === null,
    error: current?.error ?? null,
    reload,
  };
}
